import { useMemo, useCallback } from "react";
import useSWR, { useSWRConfig } from "swr";
import { fetcher } from "@/lib/fetcher";
import { useSubscription } from "./use-subscription";

interface EntitlementLimit {
  current: number;
  limit: number | null;
}

interface Entitlements {
  plan: "free" | "pro";
  accounts: EntitlementLimit;
  feeds: EntitlementLimit;
  mappings: EntitlementLimit;
  canCustomizeIcalFeed: boolean;
  canUseEventFilters: boolean;
  realtimeSync: boolean;
}

const USAGE_CACHE_KEY = "/api/entitlements";

function buildOptimisticProEntitlements(): Entitlements {
  return {
    plan: "pro",
    accounts: { current: 0, limit: null },
    feeds: { current: 0, limit: null },
    mappings: { current: 0, limit: null },
    canCustomizeIcalFeed: true,
    canUseEventFilters: true,
    realtimeSync: false,
  };
}

function useEntitlements() {
  const { data: subscription } = useSubscription();
  const {
    data: serverEntitlements,
    mutate,
    isLoading,
    error,
  } = useSWR<Entitlements>(USAGE_CACHE_KEY, fetcher);

  const data = useMemo<Entitlements | undefined>(() => {
    if (serverEntitlements) {
      return serverEntitlements;
    }

    if (subscription?.plan !== "pro") {
      return undefined;
    }

    return buildOptimisticProEntitlements();
  }, [serverEntitlements, subscription?.plan]);

  return { data, mutate, isLoading, error };
}

function useMutateEntitlements() {
  const { mutate } = useSWRConfig();

  const adjustMappingCount = useCallback(
    (delta: number) => {
      mutate<Entitlements>(
        USAGE_CACHE_KEY,
        (current) => {
          if (!current) return current;

          const nextCount = Math.max(0, current.mappings.current + delta);
          return {
            ...current,
            mappings: {
              ...current.mappings,
              current: nextCount,
            },
          };
        },
        { revalidate: false },
      );
    },
    [mutate],
  );

  const revalidateEntitlements = useCallback(() => {
    return mutate(USAGE_CACHE_KEY);
  }, [mutate]);

  return { adjustMappingCount, revalidateEntitlements };
}

function canAddMore(entitlement: EntitlementLimit | undefined): boolean {
  if (!entitlement) return true;
  if (entitlement.limit === null) return true;
  return entitlement.current < entitlement.limit;
}

function hasConfiguredSync(entitlements: Entitlements | undefined): boolean {
  return (entitlements?.mappings.current ?? 0) > 0;
}

function useHasConfiguredSync(): boolean {
  const { data } = useEntitlements();
  return hasConfiguredSync(data);
}

export {
  buildOptimisticProEntitlements,
  canAddMore,
  hasConfiguredSync,
  useHasConfiguredSync,
  useEntitlements,
  useMutateEntitlements,
  USAGE_CACHE_KEY,
};
export type { Entitlements, EntitlementLimit };
